import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { X, Settings, Cookie, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/contexts/LanguageContext';

interface CookieConsent {
  necessary: boolean;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
}

const CookieBanner = () => {
  const { t } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [consent, setConsent] = useState<CookieConsent>({
    necessary: true, // Always required
    functional: false,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    // Check if user has already given consent
    const existingConsent = localStorage.getItem('cookieConsent');
    if (!existingConsent) {
      // Show banner after 2 seconds if no consent given
      const timer = setTimeout(() => setIsVisible(true), 2000);
      return () => clearTimeout(timer);
    } else {
      // Load existing consent
      try {
        const parsed = JSON.parse(existingConsent);
        setConsent(parsed);
      } catch (error) {
        console.error('Error parsing cookie consent:', error);
        setIsVisible(true);
      }
    }
  }, []);

  const saveConsentToDatabase = async (consentData: CookieConsent, method: string = 'banner') => {
    try {
      // Generate session ID if not exists
      let sessionId = localStorage.getItem('sessionId');
      if (!sessionId) {
        sessionId = crypto.randomUUID();
        localStorage.setItem('sessionId', sessionId);
      }

      const { data: { user } } = await supabase.auth.getUser();
      
      await supabase.from('cookie_consents').insert({
        user_id: user?.id || null,
        session_id: sessionId,
        necessary: consentData.necessary,
        functional: consentData.functional,
        analytics: consentData.analytics,
        marketing: consentData.marketing,
        consent_method: method,
        user_agent: navigator.userAgent
      });
    } catch (error) {
      console.error('Error saving cookie consent to database:', error);
    }
  };

  const handleAcceptAll = async () => {
    const fullConsent = {
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true,
    };
    setConsent(fullConsent);
    localStorage.setItem('cookieConsent', JSON.stringify(fullConsent));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    await saveConsentToDatabase(fullConsent, 'accept_all');
    setIsVisible(false);
  };

  const handleRejectAll = async () => {
    const minimalConsent = {
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false,
    };
    setConsent(minimalConsent);
    localStorage.setItem('cookieConsent', JSON.stringify(minimalConsent));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    await saveConsentToDatabase(minimalConsent, 'reject_all');
    setIsVisible(false);
  };

  const handleSaveSettings = async () => {
    localStorage.setItem('cookieConsent', JSON.stringify(consent));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    await saveConsentToDatabase(consent, 'custom_settings');
    setIsVisible(false);
    setShowSettings(false);
  };

  const handleConsentChange = (type: keyof CookieConsent, value: boolean) => {
    if (type === 'necessary') return; // Can't change necessary cookies
    setConsent(prev => ({ ...prev, [type]: value }));
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Cookie className="h-5 w-5 text-primary" />
              <CardTitle className="text-lg">{t.cookies.title}</CardTitle>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsVisible(false)}
              aria-label={t.cookies.closeSettings}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {!showSettings ? (
            <>
              <p className="text-sm text-muted-foreground">
                {t.cookies.description}
              </p>
              
              <div className="flex flex-wrap gap-3">
                <Button onClick={handleAcceptAll} className="flex-1 min-w-32" aria-label={t.cookies.acceptAllLabel}>
                  {t.cookies.acceptAll}
                </Button>
                <Button 
                  variant="outline" 
                  onClick={handleRejectAll}
                  className="flex-1 min-w-32"
                  aria-label={t.cookies.rejectAllLabel}
                >
                  {t.cookies.rejectAll}
                </Button>
                <Button 
                  variant="secondary" 
                  onClick={() => setShowSettings(true)}
                  className="flex items-center gap-2"
                  aria-label={t.cookies.openSettings}
                >
                  <Settings className="h-4 w-4" />
                  {t.cookies.settings}
                </Button>
              </div>
              
              <p className="text-xs text-muted-foreground">
                {t.cookies.moreInfo}{' '}
                <Link to="/slapuku-politika" className="text-primary underline">
                  {t.footer.cookiePolicy}
                </Link>
                {' '}&{' '}
                <Link to="/privatumo-politika" className="text-primary underline">
                  {t.footer.privacyPolicy}
                </Link>
              </p>
            </>
          ) : (
            <>
              <div className="space-y-4">
                <h4 className="font-semibold">{t.cookies.categoriesTitle}</h4>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium">{t.cookies.necessary}</span>
                        <Badge variant="destructive" className="text-xs">{t.cookies.necessaryBadge}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {t.cookies.necessaryDescription}
                      </p>
                    </div>
                    <Switch checked={true} disabled />
                  </div>
                  
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium">{t.cookies.functional}</span>
                        <Badge variant="secondary" className="text-xs">{t.cookies.optionalBadge}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {t.cookies.functionalDescription}
                      </p>
                    </div>
                    <Switch 
                      checked={consent.functional} 
                      onCheckedChange={(value) => handleConsentChange('functional', value)}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium">{t.cookies.analytics}</span>
                        <Badge variant="secondary" className="text-xs">{t.cookies.optionalBadge}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {t.cookies.analyticsDescription}
                      </p>
                    </div>
                    <Switch 
                      checked={consent.analytics} 
                      onCheckedChange={(value) => handleConsentChange('analytics', value)}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium">{t.cookies.marketing}</span>
                        <Badge variant="secondary" className="text-xs">{t.cookies.optionalBadge}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {t.cookies.marketingDescription}
                      </p>
                    </div>
                    <Switch 
                      checked={consent.marketing} 
                      onCheckedChange={(value) => handleConsentChange('marketing', value)}
                    />
                  </div>
                </div>
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button onClick={handleSaveSettings} className="flex-1" aria-label={t.cookies.saveSelected}>
                  {t.cookies.saveSettings}
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => setShowSettings(false)}
                  className="flex-1"
                  aria-label={t.cookies.backToMain}
                >
                  {t.cookies.back}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CookieBanner;